import {Culture, DateTime, Translations} from "@2d-front-monorepo/core";
import {InstallationByHostData} from "@2d-front-monorepo/permits-business";
import {REACT_APP_HOSTNAME} from "../config";
import {HttpContext} from "./HttpContext";
import {reloadPermits} from "./PermitsContext";
import {userStore} from "./UserContext";

export type GlobalConfig = {
	installation: InstallationByHostData,
	locale: Culture,
	languageId: string,
	now: DateTime
}

export class GlobalContext {
	private static _data: GlobalConfig | null = null;
	private static _dateOffset: number;
	private static _initializationPromise: Promise<void> | null = null;
	private static _isInitialized = false;

	static getData(): GlobalConfig {
		if (this._data == null) {
			throw new Error("GlobalContext is not initialized");
		}

		this._data.now = DateTime.fromTimestamp(Date.now() - this._dateOffset);
		return this._data;
	}

	static initialize(): Promise<void> {
		if (this._isInitialized) {
			return Promise.resolve();
		}

		if (this._initializationPromise) {
			return this._initializationPromise;
		}

		this._initializationPromise = this.performInitialization();
		return this._initializationPromise;
	}

	static isReady(): boolean {
		return this._isInitialized;
	}

	static reset(): void {
		this._data = null;
		this._initializationPromise = null;
		this._isInitialized = false;
	}

	static tryGetData(): GlobalConfig | null {
		return this._data;
	}

	private static async performInitialization(): Promise<void> {
		try {
			const httpClient = HttpContext.getClient();
			const installationData = await httpClient.global.installation(REACT_APP_HOSTNAME);

			//TODO: resolve somehow?
			const languageId = installationData.languages[0];

			const [, translationEntries, allPermits] = await Promise.all([
				httpClient.global.ping(),
				httpClient.global.translations(installationData.id, languageId),
				reloadPermits()
			]);

			const translations = new Translations(languageId, [{
				localeId: languageId,
				translations: translationEntries
			}]);

			const culture: Culture = Culture.setCurrent(languageId, {
				messageTranslator: translations,
				currency: installationData.currency.code
			});

			culture.addCustomFormat("short-time", {
				"hour": "2-digit",
				"minute": "2-digit"
			});

			document.getElementsByTagName("html")[0].setAttribute("lang", languageId);
			document.getElementsByTagName("title")[0].textContent = installationData.name + " | Permits portal";

			this._data = {
				installation: installationData,
				locale: culture,
				languageId: languageId,
				now: installationData.now
			};

			this._dateOffset = Date.now() - (installationData.now as unknown as number);
			this._isInitialized = true;

			if (installationData.id !== allPermits?.installationId) {
				try {
					await httpClient.authentication.signOff();
				} catch {

				} finally {
					userStore.patchState(state => {
						state.authStatus = "unauthorized";
					});
				}
			}

			HttpContext.reset();

		} finally {
			this._initializationPromise = null;
		}
	}
}
